import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Search, MapPin, Loader2, Locate,
    Home as HomeIcon, LandPlot, Building, Key, Store, BedDouble, Warehouse, TreePine, Factory,
    Banknote, Scale, Compass, Sparkles, Sun, Truck, Wrench, HardHat, Ruler, FileText, Shield, Building2,
} from "lucide-react";
import { api } from "../lib/api";
import MobileScreenHeader from "../components/MobileScreenHeader";

/* ------------------------------------------------------------------
 * DiscoverLocations — reusable "Category → Select City" screen.
 * Used for both:
 *   • /discover/property/:key → property_type / listing_type filter
 *   • /discover/service/:key  → service role filter
 *
 * When a city is picked, we route to the existing results page with
 * the appropriate query params so all existing filters / pagination
 * continue to work.
 * ------------------------------------------------------------------ */

// Property + Service category dictionaries — key must match the routes
// used on MobileNativeHome + the backend filter values (property_type,
// listing_type, or service role).
const PROP_CATEGORIES = {
    flat:        { title: "Flats",       icon: Building,   type: "residential" },
    plot:        { title: "Plots",       icon: LandPlot,   type: "plot" },
    villa:       { title: "Villas",      icon: HomeIcon,   type: "villa" },
    farmhouse:   { title: "Farm Houses", icon: TreePine,   type: "farmhouse" },
    commercial:  { title: "Commercial",  icon: Store,      type: "commercial" },
    shop:        { title: "Shop / Showroom", icon: Store,  type: "commercial" }, // reuses commercial
    industrial:  { title: "Industrial",  icon: Factory,    type: "industrial" },
    pg:          { title: "PG",          icon: BedDouble,  listing: "pg" },
    rent:        { title: "For Rent",    icon: Key,        listing: "rent" },
    lease:       { title: "For Lease",   icon: Warehouse,  listing: "lease" },
};

const SERVICE_CATEGORIES = {
    "loan-agent":         { title: "Home Loan Agents",       icon: Banknote,  role: "loan_agent" },
    "loan-against":       { title: "Loan Against Property",  icon: Banknote,  role: "loan_agent" },
    "broker":             { title: "Real Estate Agents",     icon: Building2, role: "broker" },
    "advocate":           { title: "Advocates",              icon: Scale,     role: "advocate" },
    "architect":          { title: "Architects",             icon: Compass,   role: "architect" },
    "civil-engineer":     { title: "Civil Engineers",        icon: Ruler,     role: "civil_engineer" },
    "civil-contractor":   { title: "Civil Contractors",      icon: HardHat,   role: "civil_contractor" },
    "contractor":         { title: "Contractors",            icon: HardHat,   role: "civil_contractor" },
    "interior":           { title: "Interior Designers",     icon: Sparkles,  role: "interior_designer" },
    "property-valuer":    { title: "Property Valuers",       icon: Ruler,     role: "property_valuer" },
    "vastu":              { title: "Vastu Consultants",      icon: Sun,       role: "vastu_consultant" },
    "packers":            { title: "Packers & Movers",       icon: Truck,     role: "packers_movers" },
    "property-management":{ title: "Property Management",    icon: Wrench,    role: "property_manager" },
    "legal":              { title: "Legal Services",         icon: Scale,     role: "advocate" },
    "documentation":      { title: "Documentation",          icon: FileText,  role: "advocate" },
    "renovation":         { title: "Renovation",             icon: Sparkles,  role: "renovation" },
};

// Popular cities pinned at the top — filtered against actual API data so we
// never surface an "empty" popular tile.
const POPULAR_CITY_HINTS = [
    "Nagpur", "Pune", "Mumbai", "Aurangabad", "Nashik",
    "Amravati", "Wardha", "Chandrapur", "Hyderabad", "Bengaluru",
];

export default function DiscoverLocations({ kind }) {
    const { key } = useParams();
    const navigate = useNavigate();
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [geoLoading, setGeoLoading] = useState(false);

    const meta = kind === "property"
        ? (PROP_CATEGORIES[key] || { title: "Properties", icon: Building })
        : (SERVICE_CATEGORIES[key] || { title: "Services",  icon: Wrench   });
    const Icon = meta.icon;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                let items = [];
                if (kind === "property") {
                    const params = {};
                    if (meta.type)    params.type    = meta.type;
                    if (meta.listing) params.listing = meta.listing;
                    const { data } = await api.get("/properties/cities", { params });
                    items = data.items || [];
                } else {
                    const params = {};
                    if (meta.role) params.category = meta.role;
                    const { data } = await api.get("/services/cities", { params });
                    items = data.items || [];
                }
                setCities(items);
            } catch (e) {
                setCities([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [kind, key]);

    // Split cities into popular vs. all based on active data.
    const cityByName = useMemo(() => {
        const m = {};
        cities.forEach((c) => { if (c.city) m[c.city.toLowerCase()] = c; });
        return m;
    }, [cities]);

    const popular = POPULAR_CITY_HINTS
        .map((name) => cityByName[name.toLowerCase()])
        .filter(Boolean);
    const popularKeys = new Set(popular.map((c) => c.city.toLowerCase()));
    const rest = cities
        .filter((c) => !popularKeys.has(c.city.toLowerCase()))
        .sort((a, b) => a.city.localeCompare(b.city));

    const filter = (arr) => {
        const term = q.trim().toLowerCase();
        if (!term) return arr;
        return arr.filter((c) => c.city.toLowerCase().includes(term));
    };

    const goToCity = (city) => {
        if (kind === "property") {
            const params = new URLSearchParams();
            if (meta.type)    params.set("type",    meta.type);
            if (meta.listing) params.set("listing", meta.listing);
            params.set("city", city);
            navigate(`/properties?${params.toString()}`);
        } else {
            const params = new URLSearchParams();
            if (meta.role) params.set("cat", meta.role);
            params.set("city", city);
            navigate(`/services?${params.toString()}`);
        }
    };

    const useNearby = () => {
        if (!navigator.geolocation) return;
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                setGeoLoading(false);
                // Simple heuristic: try Nagpur first if available, else route without city
                const fallback = cities[0]?.city;
                if (fallback) {
                    goToCity(fallback);
                } else if (kind === "property") {
                    navigate("/properties");
                } else {
                    navigate("/services");
                }
            },
            () => {
                setGeoLoading(false);
                if (cities[0]) goToCity(cities[0].city);
            },
            { timeout: 5000 }
        );
    };

    const totalCount = cities.reduce((s, c) => s + (c.count || 0), 0);
    const filteredPopular = filter(popular);
    const filteredRest    = filter(rest);
    const showEmpty = !loading && cities.length === 0;

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-8" data-testid="discover-locations">
            <MobileScreenHeader
                title={meta.title}
                subtitle={`Select Location · ${totalCount} available`}
                sticky
                testid="discover-header"
            />

            {/* Category chip + search */}
            <div className="px-4 pt-3">
                <div className="flex items-center gap-3 rounded-2xl bg-white border p-3 shadow-sm" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #001a3d, #002657)" }}>
                        <Icon className="w-5 h-5" style={{ color: "#F5E5A5" }} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>{kind === "property" ? "Property Category" : "Service Category"}</div>
                        <div className="font-heading font-extrabold text-[14px] truncate" style={{ color: "#002657" }}>{meta.title}</div>
                    </div>
                </div>

                <div className="mt-3 flex items-center gap-2 rounded-2xl bg-white border shadow-sm px-3 py-2.5" style={{ borderColor: "rgba(0,38,87,0.1)" }} data-testid="discover-search-wrap">
                    <Search className="w-4 h-4 shrink-0" style={{ color: "rgba(0,38,87,0.5)" }} />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search city or locality"
                        data-testid="discover-search-input"
                        className="flex-1 bg-transparent focus:outline-none text-[13px] min-w-0"
                        style={{ color: "#002657" }}
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        onClick={useNearby}
                        disabled={geoLoading}
                        data-testid="discover-nearby-btn"
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest border"
                        style={{ background: "#FBF6E0", color: "#8B6712", borderColor: "rgba(218,165,32,0.4)" }}
                    >
                        {geoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Locate className="w-3 h-3" />} Nearby
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16" style={{ color: "rgba(0,38,87,0.55)" }}>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading cities…
                </div>
            ) : showEmpty ? (
                <div className="mx-4 mt-6 rounded-2xl bg-white border border-[#002657]/10 p-8 text-center" data-testid="discover-empty">
                    <MapPin className="w-8 h-8 mx-auto mb-2" style={{ color: "rgba(0,38,87,0.35)" }} />
                    <div className="font-heading font-bold text-[14px]" style={{ color: "#002657" }}>No {meta.title.toLowerCase()} yet</div>
                    <div className="text-[11px] mt-1" style={{ color: "rgba(0,38,87,0.55)" }}>Check back soon — new listings arrive daily.</div>
                </div>
            ) : (
                <>
                    {/* Popular Cities */}
                    {filteredPopular.length > 0 && (
                        <div className="mt-4 px-4">
                            <div className="text-[10px] uppercase tracking-widest font-extrabold mb-2" style={{ color: "#8B6712" }}>
                                Popular Cities
                            </div>
                            <div className="grid grid-cols-2 gap-2.5" data-testid="discover-popular">
                                {filteredPopular.map((c) => (
                                    <button
                                        key={c.city}
                                        type="button"
                                        onClick={() => goToCity(c.city)}
                                        data-testid={`discover-popular-${c.city.toLowerCase()}`}
                                        className="text-left rounded-2xl bg-white border p-3 shadow-sm active:scale-[0.98] transition-transform"
                                        style={{ borderColor: "rgba(218,165,32,0.35)" }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FBF6E0, #F5E5A5)" }}>
                                                <MapPin className="w-4 h-4" style={{ color: "#8B6712" }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-heading font-extrabold text-[13px] truncate" style={{ color: "#002657" }}>{c.city}</div>
                                                <div className="text-[10px]" style={{ color: "rgba(0,38,87,0.55)" }}>{c.count} available</div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* All Locations */}
                    {filteredRest.length > 0 && (
                        <div className="mt-5 px-4">
                            <div className="text-[10px] uppercase tracking-widest font-extrabold mb-2" style={{ color: "#8B6712" }}>
                                All Locations ({filteredRest.length})
                            </div>
                            <div className="rounded-2xl bg-white shadow-sm border overflow-hidden divide-y" style={{ borderColor: "rgba(0,38,87,0.08)" }} data-testid="discover-all">
                                {filteredRest.map((c) => (
                                    <button
                                        key={c.city}
                                        type="button"
                                        onClick={() => goToCity(c.city)}
                                        data-testid={`discover-city-${c.city.toLowerCase()}`}
                                        className="w-full flex items-center gap-3 px-4 py-3 active:bg-[#f6f7fb] text-left transition-colors"
                                    >
                                        <MapPin className="w-4 h-4 shrink-0" style={{ color: "rgba(0,38,87,0.5)" }} />
                                        <div className="flex-1 font-bold text-[13px]" style={{ color: "#002657" }}>{c.city}</div>
                                        <div className="text-[11px] font-semibold" style={{ color: "rgba(0,38,87,0.55)" }}>{c.count}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Filter-empty state */}
                    {q && filteredPopular.length === 0 && filteredRest.length === 0 && (
                        <div className="mx-4 mt-6 rounded-2xl bg-white border border-[#002657]/10 p-6 text-center text-sm" style={{ color: "rgba(0,38,87,0.55)" }} data-testid="discover-search-empty">
                            No cities matching &ldquo;{q}&rdquo;.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
